<script setup lang="ts">
import { useGameStore } from '@/stores/gameStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import AlertBox from '@/components/Elements/Alerts/AlertBox.vue'
import TutorialFrame from '@/components/Interface/Modal/Modals/Tutorial/TutorialFrame.vue'
import PlanetSizeAnimation from '@/components/Elements/Animations/Planet/PlanetSizeAnimation.vue'
import { computed } from 'vue'
import { handleNext } from '../tutorialSteps'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()

const handleNextStep = async () => {
  await handleNext('tutorial_planet_types')
}

// Map planet type IDs to translation keys
const planetNameMap: Record<number, string> = {
  1: 'map.planets.lava',
  2: 'map.planets.desert',
  3: 'map.planets.temperate',
  4: 'map.planets.oceanic',
  5: 'map.planets.gas',
  6: 'map.planets.ice',
  7: 'map.planets.ferion',
  8: 'map.planets.ringworld'
}

// Get the homeworld planet type name (translated)
const homeWorldPlanetType = computed(() => {
  if (!gameStore.player) return t('map.planets.unknown')
  const key = planetNameMap[gameStore.player.homeWorldPlanetType]
  return key ? t(key) : t('map.planets.unknown')
})

// Get the opposite (bad) planet type name (translated)
const oppositePlanetType = computed(() => {
  if (!gameStore.player) return t('map.planets.unknown')
  const key = planetNameMap[gameStore.player.homeWorldOppositePlanetType]
  return key ? t(key) : t('map.planets.unknown')
})

// Get the homeworld gravity type based on homeWorldPlanetSize (translated)
const homeWorldCompatibility = computed(() => {
  if (!gameStore.player) return t('tutorial.planetTypes.gravity.medium')

  // Map homeWorldPlanetSize to gravity type
  // Size 1 = low gravity, Size 3 = high gravity
  if (gameStore.player.homeWorldPlanetSize === 1) return t('tutorial.planetTypes.gravity.low')
  if (gameStore.player.homeWorldPlanetSize === 3) return t('tutorial.planetTypes.gravity.high')
  return t('tutorial.planetTypes.gravity.medium')
})

// Get opposite gravity type based on homeWorldOppositePlanetSize (translated)
const oppositeGravity = computed(() => {
  if (!gameStore.player) return t('tutorial.planetTypes.gravity.high')

  // Map homeWorldOppositePlanetSize to gravity type
  // Size 1 = low gravity, Size 3 = high gravity
  if (gameStore.player.homeWorldOppositePlanetSize === 1) return t('tutorial.planetTypes.gravity.low')
  if (gameStore.player.homeWorldOppositePlanetSize === 3) return t('tutorial.planetTypes.gravity.high')
  return t('tutorial.planetTypes.gravity.medium')
})

</script>

<template>
  <TutorialFrame
    video-url="https://cdn.ferion.com/videos/advisors/temperate/colonizing.webm"
  >
    <template #text>
      <p class="pb-2">
        {{ t('tutorial.planetTypes.timeToSettle') }}
      </p>
      <div class="flex justify-center items-center pb-2">
        <div class="h-24 w-24 flex justify-center items-center">
          <PlanetSizeAnimation
            v-if="gameStore.player"
            :size="gameStore.player.homeWorldPlanetSize"
            :type="gameStore.player.homeWorldPlanetType"
          />
        </div>
      </div>
      <p class="pb-2">
        <i18n-t keypath="tutorial.planetTypes.homeWorldType" tag="span">
          <template #planetType><span class="text-green-400">{{ homeWorldPlanetType }}</span></template>
          <template #gravity><span class="text-green-400">{{ homeWorldCompatibility }}</span></template>
        </i18n-t>
      </p>
      <p class="pb-2">
        <i18n-t keypath="tutorial.planetTypes.cannotLiveOn" tag="span">
          <template #planetType><span class="text-red-400">{{ oppositePlanetType }}</span></template>
          <template #gravity><span class="text-red-400">{{ oppositeGravity }}</span></template>
        </i18n-t>
      </p>
    </template>

    <template #alerts>
      <AlertBox type="info">
        {{ t('tutorial.planetTypes.chooseWisely') }}
      </AlertBox>
    </template>

    <template #buttons>
      <ActionButton @click="handleNextStep">{{ t('common.actions.gotIt') }}</ActionButton>
    </template>
  </TutorialFrame>
</template>

<style scoped lang="scss"></style>